import type { FormHelpMap } from "../../../components/formHelp";

/**
 * Popover help for the New Job form — the upstream-configurable object the
 * form consumes via its `help` prop. Keyed by control name (see
 * {@link FormHelpMap}); text lives here, not in the field components. Hosts
 * can pass a different or empty map per surface without touching the form.
 */
export const NEW_JOB_FORM_HELP: FormHelpMap = {
  propertyConfirmed: {
    title: "Why this resets",
    body: "Any change to the property address clears this checkbox, so a job can never carry a site confirmation for an address you have not seen.",
  },
  description: {
    title: "Describing the issue",
    body: "Record it as the client or tenant reported it, then add what you observed on site if you have been. Attach photos below — they usually settle scope questions before the quote.",
  },
  refInternal: {
    body: "Your own job code, e.g. AFS-2024-0001. It appears on quotes and invoices the client sees, so keep it stable once created.",
  },
  refExternal: {
    body: "The client's own reference (agent work order number, PDF job number). Used to reconcile their paperwork with yours; leave blank if none was supplied.",
  },
  priceEstimate: {
    body: "Internal-only figure in AUD, shown on the board and nav totals. It is an estimate, not a quote, and never reaches the client.",
  },
  occupantPhone: {
    body: "Only if you want the occupant called before attendance; otherwise the client contact stays the primary channel.",
  },
  accessNotes: {
    body: "Anything needed on arrival: key collection point, alarm code, pets, parking restrictions, whether the tenant will be present.",
  },
  attachments: {
    body: "Photos of the issue or PDFs such as the client's work order. Files are staged here and upload when the form is submitted.",
  },
  requireQuoteApproval: {
    title: "Quote gate",
    body: "On by default for priced work: the job starts in Drafts and nothing is scheduled until a quote is accepted. Turn off only for pre-approved work.",
  },
  timingRequirement: {
    body: "A request constraint, not a booking. Choose \"Must be completed by\" only for hard deadlines — the vendor plans capacity around it.",
  },
  scheduleAction: {
    title: "Progressing or waiting",
    body: "What we intend to do next — and waiting on something is one of the choices, so a job is never both at once. This is our plan; the client's own timing request lives on Job Details.",
  },
  scheduleDate: {
    body: "Indicative, never a promise. Pair it with a precision: pick the week or the month when the day is still a guess, rather than inventing a date that will read to the client as fixed.",
  },
  scheduleNote: {
    body: "Staff only, and never published — the client sees a sanitised label instead. Use it for the detail behind the plan: lead times, who is being chased, what would unblock it.",
  },
  scopeTaxRate: {
    body: "GST percentage applied to line items when totalling the scope. Defaults to 10% (AUD); enter a percentage, not a fraction.",
  },
};
